'use client'

import { useState } from 'react'

import cn from 'clsx'

import { useFormspark } from '@formspark/use-formspark'
import { useForm } from 'react-hook-form'
import Select from 'react-select'

import { Button, Copy } from 'shared-components'

import s from './Form.module.sass'

const InputField = ({ type, id, fieldName, required, placeholder, register, errors }: any) => (
    <div className={s.InputField}>
        <label className="smallCaps" htmlFor={id}>
            {fieldName}
            {required && <span className={s.Required}>*</span>}
        </label>
        <div>
            <input
                type={type}
                id={id}
                placeholder={placeholder}
                aria-describedby={type}
                {...register(id, { required: required ? `${fieldName} is required.` : false })}
            />
        </div>
        {errors[id] && <p className={cn(s.Error, 'smallCaps')}>{String(errors[id].message)}</p>}
    </div>
)

const FormBuilderBlock = ({ formTitle, formsparkId, formFields, formCopy }: any) => {
    if (!formsparkId) return null

    const [submitted, setSubmitted] = useState(false)
    const [submit, submitting] = useFormspark({ formId: formsparkId })
    const {
        register,
        handleSubmit,
        setValue,
        trigger,
        formState: { errors },
    } = useForm()

    const onSubmit = (data: any) => {
        submit(data)
        setSubmitted(true)
    }

    if (submitted) {
        return (
            <div className={s.Success}>
                <h2>Thank you for your submission!</h2>
                <p>Someone from our team will reach out to you shortly.</p>
            </div>
        )
    }

    return (
        <form className={s.Form} onSubmit={handleSubmit(onSubmit)}>
            {formTitle && <div className={s.FormTitle} dangerouslySetInnerHTML={{ __html: formTitle }} />}
            {Array?.isArray(formFields) &&
                formFields?.map((field) => {
                    const { inputType, _key, fieldId, placeholder, fieldName, required, options } = field ?? {}
                    const { current } = fieldId ?? {}

                    if (!inputType || !current) return null

                    if (inputType === 'textArea') {
                        return (
                            <div className={s.Textarea} key={_key}>
                                <label className="smallCaps" htmlFor={current}>
                                    {fieldName}
                                    {required && <span className={s.Required}>*</span>}
                                </label>
                                <div>
                                    <textarea
                                        id={current}
                                        placeholder={placeholder}
                                        aria-describedby="textarea"
                                        {...register(current, {
                                            required: required ? `${fieldName} is required.` : false,
                                        })}
                                    />
                                </div>
                                {errors[current] && (
                                    <p className={cn(s.Error, 'smallCaps')}>{String(errors[current].message)}</p>
                                )}
                            </div>
                        )
                    }

                    if (inputType === 'select') {
                        return (
                            <div className={s.Select} key={_key}>
                                <label className="smallCaps" htmlFor={current}>
                                    {fieldName}
                                    {required && <span className={s.Required}>*</span>}
                                </label>
                                <div>
                                    <Select
                                        className="react-select-container"
                                        classNamePrefix="react-select-form react-select"
                                        options={
                                            Array.isArray(options)
                                                ? [
                                                    { value: '', label: 'Please select' },
                                                    ...options.map((option) => ({
                                                        value: option,
                                                        label: option,
                                                    })),
                                                ]
                                                : undefined
                                        }
                                        id={current}
                                        aria-describedby="select"
                                        isSearchable={false}
                                        placeholder={placeholder}
                                        {...register(current, {
                                            validate: required
                                                ? (value) => value?.length > 0 || 'Please select an option.'
                                                : undefined,
                                        })}
                                        onChange={(option) => {
                                            setValue(current, option?.value ?? '', { shouldValidate: true })
                                            if (required) {
                                                trigger(current)
                                            }
                                        }}
                                    />
                                </div>
                                {errors[current] && (
                                    <p className={cn(s.Error, 'smallCaps')}>{String(errors[current].message)}</p>
                                )}
                            </div>
                        )
                    }

                    if (inputType === 'checkbox' || inputType === 'radio') {
                        return (
                            <div className={s[inputType]} key={_key}>
                                {fieldName !== 'Disclaimer' && (
                                    <label className="smallCaps" htmlFor={current}>
                                        {fieldName}
                                        {required && <span className={s.Required}>*</span>}
                                    </label>
                                )}
                                <div>
                                    {Array?.isArray(options) &&
                                        options?.map((option, index) => (
                                            <div key={index}>
                                                <input
                                                    type={inputType}
                                                    id={`${current}-${index}`}
                                                    value={option}
                                                    {...register(current, {
                                                        validate: required
                                                            ? (value) =>
                                                                value?.length > 0 ||
                                                                'At least one option must be selected.'
                                                            : undefined,
                                                    })}
                                                />
                                                <label className="smallCaps" htmlFor={`${current}-${index}`}>
                                                    {option}
                                                </label>
                                            </div>
                                        ))}
                                </div>
                                {errors[current] && (
                                    <p className={cn(cn(s.Error, 'smallCaps'), 'smallCaps')}>
                                        {String(errors[current].message)}
                                    </p>
                                )}
                            </div>
                        )
                    }

                    return (
                        <InputField
                            key={_key}
                            type={inputType}
                            id={current}
                            fieldName={fieldName}
                            required={required}
                            placeholder={placeholder}
                            register={register}
                            errors={errors}
                        />
                    )
                })}
            {formCopy && (
                <div className={s.FormCopy}>
                    <Copy copy={formCopy} size="small" />
                </div>
            )}
            <div className={cn(s.Submit, submitting && s.Submitting)}>
                <Button type="submit" title="Submit" />
            </div>
        </form>
    )
}

export default FormBuilderBlock
